import { LinearProgressOverlay } from '@campfire/linear-progress-overlay';
import { Box, Switch, Theme, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Visibility, EventAvailable, Schedule } from '@material-ui/icons';
import React, { useEffect, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { GetActivityCancellationAndClosureReasons } from './__generated__/GetActivityCancellationAndClosureReasons';
import { ActivityReasonEditForm } from './ActivityReasonEditForm';
import { ActivityReasonType } from './admin-console-operations-model';
import {
  GET_ACTIVITY_CANCELLATION_CLOSURE_REASONS,
  GET_ADMIN_CONSOLE_OPS_SESSION_VISIBILITY,
  GET_ADMIN_CONSOLE_OPS_CALENDAR_INVITES,
  GET_ADMIN_CONSOLE_OPS_ASSIGNED_ROSTERING_VIEW,
  GET_ADMIN_CONSOLE_OPS_ROSTER_COMMENTS,
} from './admin-console-operations.gql';
import { GetAdminConsoleOpsSessionVisibility } from './__generated__/GetAdminConsoleOpsSessionVisibility';
import { GetAdminConsoleOpsCalendarInvites } from './__generated__/GetAdminConsoleOpsCalendarInvites';
import { GetAdminConsoleOpsAssignedRosteringView } from './__generated__/GetAdminConsoleOpsAssignedRosteringView';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { PreventVolunteerAvailabilitySubmission } from '../admin-console-activity-reporting/PreventVolunteerAvailabilitySubmission';
import { SeeRosteredDatesOnly } from '../admin-console-activity-reporting/SeeRosteredDatesOnly';
import { AvailabilityRestriction } from './AvailabilityRestriction';
import { SessionLimits } from './SessionLimits';
import { RequireTasksBeforeJoining } from './RequireTasksBeforeJoining';
import { DefaultRosterStartDate } from './DefaultRosterStartDate';
import { AddressHiding } from './AddressHiding';
import { AllowIndividualShiftTimes } from './AllowIndividualShiftTimes';
import { ShowCapacityStatus } from './ShowCapacityStatus';
import { BlockFullSessionAvailability } from './BlockFullSessionAvailability';
import { ApplicationQuestionEditForm } from '../admin-console-application-questions/ApplicationQuestionEditForm';
import { GET_GLOBAL_ACTIVITY_APPLICATION_QUESTIONS } from '../admin-console-application-questions/application-questions.gql';
import { GetGlobalActivityApplicationQuestions } from '../admin-console-application-questions/__generated__/GetGlobalActivityApplicationQuestions';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      backgroundColor: '#F9F9F9',
      minHeight: 'calc(100% - 20px)',
    },
    contentContainer: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '2.25rem',
    },
    heading: {
      alignItems: 'center',
      paddingBottom: '8px',
      borderBottom: '1px solid #efefef',
    },
    headingTitle: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      paddingBottom: '5px',
      fontSize: 20,
    },
    infoBody: {
      color: theme.alert.blue.dark,
      fontSize: '14px',
      fontWeight: 400,
    },
  })
);

const AdminConsoleOperationsPanel = () => {
  const classes = useStyles();
  const { setSnackbar } = useSnackbar();
  const [allowSessionVis, setAllowSessionVis] = React.useState(false);
  const [calendarInvitesEnabled, setCalendarInvitesEnabled] = React.useState(false);
  const [assignedRosteringView, setAssignedRosteringView] = React.useState(false);
  const [rosterCommentsEnabled, setRosterCommentsEnabled] = React.useState(false);
  const { data: allReasonsResponse, loading: allReasonsLoading, refetch: refetchReasons } = useCampfireQuery<
    GetActivityCancellationAndClosureReasons,
    undefined
  >(GET_ACTIVITY_CANCELLATION_CLOSURE_REASONS);
  const apiUrl = useApiUrl();
  const campfireMutation = useCampfireFetch({ defer: true });

  const { data: applicationQuestionsResponse, refetch: refetchApplicationQuestions } = useCampfireQuery<
    GetGlobalActivityApplicationQuestions,
    undefined
  >(GET_GLOBAL_ACTIVITY_APPLICATION_QUESTIONS);

  const initialApplicationQuestions = useMemo(() => {
    if (!applicationQuestionsResponse?.vm.globalActivityApplicationQuestions) return { applicationQuestions: [] };
    return {
      applicationQuestions: applicationQuestionsResponse.vm.globalActivityApplicationQuestions.map((question) => ({
        activityApplicationQuestionId: question.activityApplicationQuestionId,
        label: question.label,
        type: question.type,
        required: question.required,
        order: question.order,
      })),
    };
  }, [applicationQuestionsResponse]);

  const initialCancellationReasonValues = useMemo(() => {
    if (!allReasonsResponse?.vm.activityCancellationReasons) return { reasons: [] };
    return {
      reasons: allReasonsResponse.vm.activityCancellationReasons.map((reason) => {
        return {
          activityCancellationReasonId: reason.activityCancellationReasonId,
          label: reason.label,
          listId: uuidv4(),
        };
      }),
    };
  }, [allReasonsResponse]);

  const initialClosureReasonValues = useMemo(() => {
    if (!allReasonsResponse?.vm.activityClosureReasons) return { reasons: [] };
    return {
      reasons: allReasonsResponse.vm.activityClosureReasons.map((reason) => {
        return {
          activityClosureReasonId: reason.activityClosureReasonId,
          label: reason.label,
          listId: uuidv4(),
        };
      }),
    };
  }, [allReasonsResponse]);

  const { data: opsSessionVisibility, refetch: opsSessionVisibilityRefetch } = useCampfireQuery<
    GetAdminConsoleOpsSessionVisibility,
    undefined
  >(GET_ADMIN_CONSOLE_OPS_SESSION_VISIBILITY);

  const { data: opsCalendarInvites, refetch: opsCalendarInvitesRefetch } = useCampfireQuery<
    GetAdminConsoleOpsCalendarInvites,
    undefined
  >(GET_ADMIN_CONSOLE_OPS_CALENDAR_INVITES);

  const { data: opsAssignedRosteringView, refetch: opsAssignedRosteringViewRefetch } = useCampfireQuery<
    GetAdminConsoleOpsAssignedRosteringView,
    undefined
  >(GET_ADMIN_CONSOLE_OPS_ASSIGNED_ROSTERING_VIEW);

  const { data: opsRosterComments, refetch: opsRosterCommentsRefetch } = useCampfireQuery(
    GET_ADMIN_CONSOLE_OPS_ROSTER_COMMENTS
  );

  useEffect(() => {
    setAllowSessionVis(opsSessionVisibility?.vm.configItem?.configItemValue?.flagValue ?? false);
  }, [opsSessionVisibility]);

  useEffect(() => {
    setCalendarInvitesEnabled(opsCalendarInvites?.vm.configItem?.configItemValue?.flagValue ?? false);
  }, [opsCalendarInvites]);

  useEffect(() => {
    setAssignedRosteringView(opsAssignedRosteringView?.vm.configItem?.configItemValue?.flagValue ?? false);
  }, [opsAssignedRosteringView]);

  useEffect(() => {
    setRosterCommentsEnabled(opsRosterComments?.vm.configItem?.configItemValue?.flagValue ?? false);
  }, [opsRosterComments]);

  const onSessionVisChange = () => {
    campfireMutation
      .run({
        url: `${apiUrl}/vm/admin-console/config`,
        method: 'post',
        data: {
          value: !allowSessionVis,
          type: 'operations-allow-roster-session-visibility',
        },
      })
      .then(() => {
        if (opsSessionVisibilityRefetch) {
          setTimeout(opsSessionVisibilityRefetch, 0);
        }
        setSnackbar({
          variant: 'success',
          open: true,
          message: 'Settings updated',
        });
      });

    setAllowSessionVis(!allowSessionVis);
  };

  const onCalendarInvitesChange = () => {
    campfireMutation
      .run({
        url: `${apiUrl}/vm/admin-console/config`,
        method: 'post',
        data: {
          value: !calendarInvitesEnabled,
          type: 'operations-calendar-invites-enabled',
        },
      })
      .then(() => {
        if (opsCalendarInvitesRefetch) {
          setTimeout(opsCalendarInvitesRefetch, 0);
        }
        setSnackbar({
          variant: 'success',
          open: true,
          message: 'Calendar invite settings updated',
        });
      });

    setCalendarInvitesEnabled(!calendarInvitesEnabled);
  };

  const onAssignedRosteringViewChange = () => {
    campfireMutation
      .run({
        url: `${apiUrl}/vm/admin-console/config`,
        method: 'post',
        data: {
          value: !assignedRosteringView,
          type: 'operations-assigned-rostering-view',
        },
      })
      .then(() => {
        if (opsAssignedRosteringViewRefetch) {
          setTimeout(opsAssignedRosteringViewRefetch, 0);
        }
        setSnackbar({
          variant: 'success',
          open: true,
          message: 'Assigned rostering view settings updated',
        });
      });

    setAssignedRosteringView(!assignedRosteringView);
  };

  const onRosterCommentsChange = () => {
    campfireMutation
      .run({
        url: `${apiUrl}/vm/admin-console/config`,
        method: 'post',
        data: {
          value: !rosterCommentsEnabled,
          type: 'allow-roster-comments',
        },
      })
      .then(() => {
        if (opsRosterCommentsRefetch) {
          setTimeout(opsRosterCommentsRefetch, 0);
        }
        setSnackbar({
          variant: 'success',
          open: true,
          message: 'Roster comments settings updated',
        });
      });

    setRosterCommentsEnabled(!rosterCommentsEnabled);
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.heading}>
        <Typography variant='h2' className={classes.headingTitle}>
          Operations
        </Typography>
        <Typography variant={'body2'}>{'Manage settings relating to operations, activities and rosters'}</Typography>
      </Box>
      <Box style={{ marginTop: '25px' }} className={classes.contentContainer}>
        <LinearProgressOverlay isLoading={allReasonsLoading} />
        <ActivityReasonEditForm
          initialValues={initialCancellationReasonValues}
          type={ActivityReasonType.cancellation}
          refetchReasons={refetchReasons}
        />
      </Box>
      <Box style={{ marginTop: '25px' }} className={classes.contentContainer}>
        <ActivityReasonEditForm
          initialValues={initialClosureReasonValues}
          type={ActivityReasonType.closure}
          refetchReasons={refetchReasons}
        />
      </Box>
      <Box style={{ marginTop: '25px' }} className={classes.contentContainer}>
        <Typography style={{ fontWeight: 600, fontSize: '16px', display: 'flex', alignItems: 'center' }}>
          <Visibility style={{ fontSize: '14px', paddingRight: '5px' }} /> Visibility
        </Typography>
        <Typography variant={'body2'} style={{ fontSize: '14px', color: '#808080' }}>
          {'Options relating to visibility of activity and roster detail'}
        </Typography>
        <Box marginTop='1.5rem'>
          <Box mt={2}>
            <Box display='flex' alignItems={'center'}>
              <Switch
                checked={allowSessionVis}
                onChange={onSessionVisChange}
                color='primary'
                inputProps={{ 'aria-label': 'allow comments' }}
              />
              <Box ml={1}>
                <Typography
                  style={{
                    fontWeight: 500,
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Roster Session Visibility
                </Typography>
                <Typography variant={'body2'} style={{ fontSize: '13px', color: '#808080' }}>
                  {'Users can see details of sessions that other team members are rostered on for'}
                </Typography>
              </Box>
            </Box>
          </Box>
          <SeeRosteredDatesOnly />
          <AddressHiding />
          <Box marginTop='1.5rem'>
            <DefaultRosterStartDate />
          </Box>
        </Box>
      </Box>
      <Box style={{ marginTop: '25px' }} className={classes.contentContainer}>
        <Typography style={{ fontWeight: 600, fontSize: '16px', display: 'flex', alignItems: 'center' }}>
          <EventAvailable style={{ fontSize: '14px', paddingRight: '5px' }} /> Rosters
        </Typography>
        <Typography variant={'body2'} style={{ fontSize: '14px', color: '#808080' }}>
          {'Options relating to roster notifications and calendar integration'}
        </Typography>
        <Box marginTop='1.5rem'>
          <Box mt={2}>
            <Box display='flex' alignItems={'center'}>
              <Switch
                checked={calendarInvitesEnabled}
                onChange={onCalendarInvitesChange}
                color='primary'
                inputProps={{ 'aria-label': 'calendar invites toggle' }}
              />
              <Box ml={1}>
                <Typography
                  style={{
                    fontWeight: 500,
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Calendar Invites
                </Typography>
                <Typography variant={'body2'} style={{ fontSize: '13px', color: '#808080' }}>
                  {
                    'Include calendar invites with roster notification emails to help volunteers add events to their calendars'
                  }
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box mt={2}>
            <Box display='flex' alignItems={'center'}>
              <Switch
                checked={assignedRosteringView}
                onChange={onAssignedRosteringViewChange}
                color='primary'
                inputProps={{ 'aria-label': 'assigned rostering view toggle' }}
              />
              <Box ml={1}>
                <Typography
                  style={{
                    fontWeight: 500,
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Assigned Rostering View
                </Typography>
                <Typography variant={'body2'} style={{ fontSize: '13px', color: '#808080' }}>
                  Enable the assigned rostering view in the calendar roster screen
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box mt={2}>
            <Box display='flex' alignItems={'center'}>
              <Switch
                checked={rosterCommentsEnabled}
                onChange={onRosterCommentsChange}
                color='primary'
                inputProps={{ 'aria-label': 'roster comments toggle' }}
              />
              <Box ml={1}>
                <Typography
                  style={{
                    fontWeight: 500,
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Roster Comments
                </Typography>
                <Typography variant={'body2'} style={{ fontSize: '13px', color: '#808080' }}>
                  Allow volunteers to post comments on rosters. Can be overridden per activity.
                </Typography>
              </Box>
            </Box>
          </Box>
          <AllowIndividualShiftTimes />
        </Box>
      </Box>
      <Box style={{ marginTop: '25px' }} className={classes.contentContainer}>
        <Typography style={{ fontWeight: 600, fontSize: '16px', display: 'flex', alignItems: 'center' }}>
          <Schedule style={{ fontSize: '14px', paddingRight: '5px' }} /> Availability
        </Typography>
        <Typography variant={'body2'} style={{ fontSize: '14px', color: '#808080' }}>
          {'Options relating to volunteer availability submission'}
        </Typography>
        <PreventVolunteerAvailabilitySubmission />
        <ShowCapacityStatus />
        <BlockFullSessionAvailability />
        <Box marginTop='1.5rem'>
          <AvailabilityRestriction />
        </Box>
        <Box marginTop='1.5rem'>
          <SessionLimits />
          <RequireTasksBeforeJoining />
        </Box>
      </Box>
      <Box style={{ marginTop: '25px' }} className={classes.contentContainer}>
        <ApplicationQuestionEditForm
          initialValues={initialApplicationQuestions}
          refetchQuestions={refetchApplicationQuestions}
        />
      </Box>
    </Box>
  );
};

export { AdminConsoleOperationsPanel };
